import { useState, useEffect } from 'react';
import { Button, IconButton, Box, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { LibraryBooks, TextSnippet } from '@mui/icons-material';
import { checkFile, ConditionalWrapper, trackEvent } from '../Utilities';
import { isMobile, useMobileOrientation } from 'react-device-detect';
import dayjs from 'dayjs';
import DatePicker from '../DatePicker';
import CameraSelection from '../CameraSelection';
import PdfDialog from './PdfDialog';

export default function Diary({ timestamp, setTimestamp, loading, disabled, shouldDisableDate, siderWidth, footerOpen, setFooterOpen }) {
    
    const { CAMERAS, LAYOUT: { TITLE }, BASEURL } = window.conf;
    const { t } = useTranslation();
    const { isPortrait } = useMobileOrientation();

    const [cameraId, setCameraId] = useState(CAMERAS?.length ? CAMERAS[0].id : 0);
    const [openDialog, setOpenDialog] = useState(false);
    const [fileExists, setFileExists] = useState(false);
    const [alertMessage, setAlertMessage] = useState(null);

    const moreThanOneCam = CAMERAS.length > 1
    const selectedCam = CAMERAS.find(camera => camera.id === cameraId)
    const selectedDate = timestamp.format("YY-MM-DD")
    const filePath = `${BASEURL}cam${selectedCam.id + 1}/reports/${selectedDate}.pdf` // example: `https://pdfobject.com/pdf/sample.pdf`
    const today = dayjs()
    const title = TITLE ? `${TITLE}_` : ""
    const camera = selectedCam.name 
        ? `${selectedCam.name}_` 
        : moreThanOneCam 
            ? `${t("events.camera")} ${selectedCam.id + 1}_` 
            : ""
    const targetFilename = `${title}${camera}${selectedDate}` || t("events.construction_diary.construction_diary")

    useEffect(() => {
        const checkPdf = async() => {
            const { exists, contentType } = await checkFile(filePath)
            if (exists && contentType === "pdf") {
                setAlertMessage(null)
                setFileExists(true)
            } else {
                if (today.isSame(timestamp, 'day')) {
                    setAlertMessage({ type: 'warning', message: t('events.construction_diary.no_data_today') })
                } else {
                    setAlertMessage({ type: 'error', message: t('msg.no_file') })
                }
                setFileExists(false)
            }
        }
        checkPdf()
    }, [filePath, timestamp]) // eslint-disable-line react-hooks/exhaustive-deps

    const onOpenDialog = () => {
        setOpenDialog(true)
        trackEvent(t("events.construction_diary.construction_diary", { lng: "de" }), t("actions.create", { lng: "de" }))
    }

    function onOpenDrawer() {
        if (setFooterOpen && !footerOpen) {
            setFooterOpen()
        }
    } 

    return (
        <>
            {isMobile && isPortrait && !footerOpen ? (
                <IconButton
                    aria-label="construction diary"
                    color="primary"
                    onClick={onOpenDrawer}
                    sx={{ pointerEvents: "all" }}
                    disabled={loading || disabled}
                >
                    <TextSnippet sx={{ fontSize: "2rem" }} />
                </IconButton>
            ) : (
                <Box sx={{ width: "100%", display: "flex", flexDirection: "column" }}>

                    {moreThanOneCam && (
                        <CameraSelection
                            value={cameraId}
                            onChange={setCameraId}
                        />
                    )}

                    <DatePicker
                        timestamp={timestamp}
                        setTimestamp={setTimestamp}
                        loading={loading}
                        shouldDisableDate={shouldDisableDate}
                        siderWidth={siderWidth}
                        pb={2}
                        withoutToday
                    />

                    <ConditionalWrapper
                        condition={timestamp.isSame(today, 'day')}
                        wrapper={children => <Tooltip title={t("events.construction_diary.no_data_today")} placement="top" arrow>{children}</Tooltip>}
                    >
                        <Box component="span">
                            <Button
                                onClick={onOpenDialog}
                                startIcon={<LibraryBooks />}
                                disabled={loading || disabled || timestamp.isSame(today, 'day')}
                                variant="contained"
                                size='large'
                                fullWidth
                            >
                                {t("actions.create")}
                            </Button>
                        </Box>
                    </ConditionalWrapper>

                </Box>
            )}

            <PdfDialog
                open={openDialog}
                setOpen={setOpenDialog}
                alertMessage={alertMessage}
                fileExists={fileExists}
                filePath={filePath}
                targetFilename={targetFilename}
            />
        </>
    )
}